import { useState } from 'react';
import { Loader2, Grid3x3, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

export function ReconciliationCoveragePanel() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.reconciliationCoverage();
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const r = result;
  const rows = (r && r.rows) || [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Grid3x3 className="h-5 w-5 text-blue-600" />
          Reconciliation Coverage (Read-Only)
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Per entity, per bank account: transaction count, matched / unmatched / ignored,
          oldest and newest dates, and any calendar month between them with zero transactions
          (the feed-gap signal). Unassigned entity or account rows are surfaced first, never
          folded into a real entity. Read-only, no writes.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <Button onClick={run} disabled={loading} size="sm" data-testid="recon-coverage-run">
          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Grid3x3 className="h-4 w-4 mr-2" />}
          Run Coverage Scan
        </Button>

        {r && (
          <div className="space-y-3" data-testid="recon-coverage-result">
            <div className="flex flex-wrap gap-2 text-xs text-muted-foreground items-center">
              <span>Generated {r.generated_at}</span>
              <span>|</span>
              <span>{r.total_transactions} transactions</span>
              <span>|</span>
              <span>{r.group_count} entity/account groups</span>
              {r.account_count != null && (
                <span>
                  {r.account_count} accounts
                  {r.visible_account_count != null && r.visible_account_count !== r.account_count && (
                    <span className="text-blue-600">
                      {' '}({r.visible_account_count} visible, {r.account_count - r.visible_account_count} hidden/merged)
                    </span>
                  )}
                </span>
              )}
              {r.zero_transaction_account_count > 0 && (
                <Badge variant="destructive">
                  {r.zero_transaction_account_count} account(s) with zero transactions
                </Badge>
              )}
              {r.unparseable_date_count > 0 && (
                <Badge variant="outline" className="text-amber-600">
                  {r.unparseable_date_count} unparseable dates
                </Badge>
              )}
              {r.ignored_state_exists === false && (
                <Badge variant="outline">no ignore state in model</Badge>
              )}
            </div>

            {rows.length === 0 ? (
              <p className="text-sm text-muted-foreground">No transactions found.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs border">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left p-2">Entity</th>
                      <th className="text-left p-2">Account</th>
                      <th className="text-right p-2">Tx</th>
                      <th className="text-right p-2">Matched</th>
                      <th className="text-right p-2">Unmatched</th>
                      <th className="text-right p-2" title="Reconciled with no invoice counterpart">Manual (no inv)</th>
                      <th className="text-right p-2">Ignored</th>
                      <th className="text-left p-2">Oldest</th>
                      <th className="text-left p-2">Newest</th>
                      <th className="text-right p-2">Zero-tx months</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, i) => (
                      <tr
                        key={i}
                        className={`border-t ${row.no_transactions ? 'bg-red-50 dark:bg-red-950/20' : row.is_unassigned ? 'bg-amber-50 dark:bg-amber-950/20' : ''}`}
                        data-testid="recon-coverage-row"
                      >
                        <td className="p-2">
                          {(row.is_unassigned || row.no_transactions) && (
                            <AlertTriangle className={`h-3 w-3 inline mr-1 ${row.no_transactions ? 'text-red-500' : 'text-amber-500'}`} />
                          )}
                          {row.entity_name}
                        </td>
                        <td className="p-2">
                          {row.account_label}
                          {row.bank_name && row.bank_name !== row.account_label && (
                            <span className="text-muted-foreground"> ({row.bank_name})</span>
                          )}
                          {row.no_transactions && (
                            <Badge variant="destructive" className="ml-2">no transactions</Badge>
                          )}
                        </td>
                        <td className="p-2 text-right font-mono">{row.tx_count}</td>
                        <td className="p-2 text-right font-mono text-green-600">{row.matched}</td>
                        <td className="p-2 text-right font-mono text-orange-600">{row.unmatched}</td>
                        <td className="p-2 text-right font-mono">{row.manual_no_invoice ?? 0}</td>
                        <td className="p-2 text-right font-mono">{row.ignored}</td>
                        <td className="p-2 font-mono">{row.oldest || '—'}</td>
                        <td className="p-2 font-mono">{row.newest || '—'}</td>
                        <td className="p-2 text-right">
                          {row.zero_tx_month_count > 0 ? (
                            <Badge variant="destructive" title={row.zero_tx_months.join(', ')}>
                              {row.zero_tx_month_count}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground">0</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {rows.some((row) => row.zero_tx_month_count > 0) && (
              <p className="text-xs text-muted-foreground">
                A non-zero "Zero-tx months" count means the account has one or more calendar
                months between its oldest and newest transaction with no transactions at all,
                a likely feed gap (outage or cursor reset). Hover the badge for the month list.
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
